/**
 * Competencies API Client
 *
 * API functions for competency management.
 * Supports:
 * - Basiscompetenties: General competencies manually assigned to employees
 * - Vakcompetenties: Task-specific competencies linked to tasks, auto-assigned via task assignment
 */

import { apiRequest } from './api';

/**
 * Linked task info for vak competencies
 */
export interface LinkedTask {
  id: number;
  name: string;
}

/**
 * Competency with full details
 */
export interface Competency {
  id: number;
  name: string;
  beschrijving: string | null;
  type: 'basis' | 'vak';
  examples: string | null;
  isActive: boolean;
  sortOrder: number;
  linkedTasks: LinkedTask[];
  employeeCount: number;
  taskCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Employee competencies grouped by type
 */
export interface EmployeeCompetencies {
  basis: Competency[];
  vak: Competency[];
}

/**
 * Input for creating a competency
 */
export interface CreateCompetencyInput {
  name: string;
  beschrijving?: string | null;
  type: 'basis' | 'vak';
  examples?: string | null;
  taskIds?: number[]; // For vak competencies
}

/**
 * Input for updating a competency
 */
export interface UpdateCompetencyInput {
  name?: string;
  beschrijving?: string | null;
  type?: 'basis' | 'vak';
  examples?: string | null;
  isActive?: boolean;
  sortOrder?: number;
  taskIds?: number[]; // For vak competencies
}

/**
 * Result of toggle active operation
 */
export interface ToggleActiveResult {
  isActive: boolean;
}

/**
 * Result of bulk employee competency assignment
 */
export interface BulkAssignmentResult {
  assigned: number[];
  removed: number[];
}

/**
 * Competencies API functions
 */
/**
 * Score criterion for a task/competency level
 */
export interface ScoreCriterion {
  scoreLevel: number;
  label: string;
  description: string;
}

/**
 * Base matrix item with scoring criteria
 */
export interface ScoringMatrixItem {
  id: number;
  name: string;
  beschrijving: string | null;
  criteria: ScoreCriterion[];
}

/**
 * Task with scoring criteria for matrix view
 */
export interface TaskMatrixItem extends ScoringMatrixItem {
  weging: number;
}

/**
 * Competency with scoring criteria for matrix view
 */
export interface CompetencyMatrixItem extends ScoringMatrixItem {
  type: 'basis' | 'vak';
}

/**
 * Full scoring matrix with tasks and competencies
 */
export interface ScoringMatrix {
  tasks: TaskMatrixItem[];
  competencies: {
    basis: CompetencyMatrixItem[];
    vak: CompetencyMatrixItem[];
  };
  generatedAt: string;
}

/**
 * Legacy type alias for backwards compatibility
 * @deprecated Use ScoringMatrix instead
 */
export type CompetencyMatrix = ScoringMatrix;

export const competenciesApi = {
  // ============================================
  // READ OPERATIONS
  // ============================================

  /**
   * Get all competencies
   */
  async getAll(): Promise<Competency[]> {
    return apiRequest<Competency[]>('/competencies');
  },

  /**
   * Get scoring matrix with tasks and competencies including their scoring criteria
   */
  async getScoringMatrix(): Promise<ScoringMatrix> {
    return apiRequest<ScoringMatrix>('/competencies/scoring-matrix');
  },

  /**
   * @deprecated Use getScoringMatrix instead
   */
  async getMatrix(): Promise<ScoringMatrix> {
    return this.getScoringMatrix();
  },

  /**
   * Get competency by ID
   */
  async getById(id: number): Promise<Competency> {
    return apiRequest<Competency>(`/competencies/${id}`);
  },

  /**
   * Get basis (general) competencies
   */
  async getBasis(activeOnly = true): Promise<Competency[]> {
    const url = activeOnly ? '/competencies/basis' : '/competencies/basis?activeOnly=false';
    return apiRequest<Competency[]>(url);
  },

  /**
   * Get vak (task-specific) competencies
   */
  async getVak(activeOnly = true): Promise<Competency[]> {
    const url = activeOnly ? '/competencies/vak' : '/competencies/vak?activeOnly=false';
    return apiRequest<Competency[]>(url);
  },

  /**
   * Get competencies linked to a specific task
   */
  async getForTask(taskId: number): Promise<Competency[]> {
    return apiRequest<Competency[]>(`/competencies/task/${taskId}`);
  },

  /**
   * Get all competencies for an employee (basis + vak via assigned tasks).
   *
   * `scoringModel` (Epic 4, Story 4.2, AC #3, AD-4) — optioneel, uitsluitend
   * gebruikt door de wizard (Step3Competencies) om voor `core_tasks_v2`-
   * beoordelingen de compactere set (via toegewezen kerntaken) op te halen.
   * Weglaten geeft het bestaande `tasks_v1`-gedrag (ongewijzigd).
   */
  async getForEmployee(employeeId: number, scoringModel?: 'tasks_v1' | 'core_tasks_v2'): Promise<EmployeeCompetencies> {
    const query = scoringModel ? `?scoringModel=${scoringModel}` : '';
    return apiRequest<EmployeeCompetencies>(`/competencies/employee/${employeeId}${query}`);
  },

  /**
   * Get vak competencies available for linking to tasks
   */
  async getAvailableForTask(): Promise<Competency[]> {
    return apiRequest<Competency[]>('/competencies/available-for-task');
  },

  // ============================================
  // WRITE OPERATIONS
  // ============================================

  /**
   * Create a new competency
   */
  async create(input: CreateCompetencyInput): Promise<Competency> {
    return apiRequest<Competency>('/competencies', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Update a competency
   */
  async update(id: number, input: UpdateCompetencyInput): Promise<Competency> {
    return apiRequest<Competency>(`/competencies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  /**
   * Toggle competency active status (soft delete)
   */
  async toggleActive(id: number): Promise<ToggleActiveResult> {
    return apiRequest<ToggleActiveResult>(`/competencies/${id}/toggle-active`, {
      method: 'PATCH',
    });
  },

  /**
   * Delete a competency (only if not in use)
   */
  async delete(id: number): Promise<void> {
    return apiRequest<void>(`/competencies/${id}`, {
      method: 'DELETE',
    });
  },

  // ============================================
  // EMPLOYEE COMPETENCY ASSIGNMENT
  // ============================================

  /**
   * Assign a basis competency to an employee
   */
  async assignToEmployee(employeeId: number, competencyId: number): Promise<void> {
    return apiRequest<void>(`/competencies/employee/${employeeId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ competencyId }),
    });
  },

  /**
   * Remove a basis competency from an employee
   */
  async removeFromEmployee(employeeId: number, competencyId: number): Promise<void> {
    return apiRequest<void>(`/competencies/employee/${employeeId}/${competencyId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Bulk replace basis competencies for an employee
   */
  async bulkReplaceForEmployee(employeeId: number, competencyIds: number[]): Promise<BulkAssignmentResult> {
    return apiRequest<BulkAssignmentResult>(`/competencies/employee/${employeeId}/bulk`, {
      method: 'PUT',
      body: JSON.stringify({ competencyIds }),
    });
  },

  // ============================================
  // TASK-COMPETENCY LINKING
  // ============================================

  /**
   * Link vak competencies to a task
   */
  async linkToTask(taskId: number, competencyIds: number[]): Promise<void> {
    return apiRequest<void>(`/competencies/task/${taskId}/link`, {
      method: 'PUT',
      body: JSON.stringify({ competencyIds }),
    });
  },
};
